import React, { useEffect, useState } from 'react';

// Hooks
import { useSelector, useDispatch } from 'react-redux';

// Utils
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGE_SIZES } from '~/utils/constants';

// MUI Components
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
  MenuItem,
  TextField,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import DataGrid from '~/components/General/OlcDataGrid';

// Components
import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcTablePagination from '~/components/General/OlcTablePagination';

// Icons
import { DeleteCIcon, GeneralPencilCIcon } from '~/assets/images';
import CheckSettingsIcon from '~/assets/images/settings/check.svg';
import XSettingsIcon from '~/assets/images/settings/x.svg';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Close as CloseIcon } from '@mui/icons-material';
import { stringLength } from '~/utils/helperFunctions';
// Actions
import { updateCustomField, fetchCustomFields } from '~/redux/actions/customFields-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Styles
import './styles.scss';

const CustomTable = ({
  pagination,
  updatePagination,
  setSelectedCustomField,
  setEditModal,
  initialLoad,
  onDelete,
  customFieldTypes = [],
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeRowId, setActiveRowId] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [showNoRows, setShowNoRows] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const open = Boolean(anchorEl);

  const dispatch = useDispatch();

  const rows = useSelector((state) => state.customFieldsReducers.customFields) || [];
  const to = useSelector((state) => state.customFieldsReducers.to);
  const from = useSelector((state) => state.customFieldsReducers.from);
  const lastPage = useSelector((state) => state.customFieldsReducers.lastPage || 1);
  const currentPage = useSelector((state) => state.customFieldsReducers.currentPage || 1);
  const perPage = useSelector((state) => state.customFieldsReducers.perPage) || pagination.pageSize;

  const isLoading = useSelector((state) => state.customFieldsReducers.loading);

  const rowsCount = useSelector((state) => state.customFieldsReducers.rowsCount) || 0;

  const sortedRows = [...rows].sort((a, b) => a?.value?.localeCompare(b?.value)) || [];
  const showLoader = initialLoad || isLoading || paginationLoading;
  const pageSizes = DEFAULT_PAGE_SIZES;

  // Reset pagination loading when new data arrives
  useEffect(() => {
    setPaginationLoading(false);
  }, [rows]);

  const handleChangePage = (event, newPage) => {
    setPaginationLoading(true);
    updatePagination({
      page: newPage,
      pageSize: pagination.pageSize,
    });
  };
  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setActiveRowId(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveRowId(null);
  };

  const handleDelete = (row) => {
    onDelete(row);
    handleMenuClose();
  };

  const handleSave = async () => {
    if (!editingValue.trim()) {
      dispatch(failure('Custom field name cannot be empty'));
      return;
    }
    try {
      setSaving(true);
      const payload = { customFieldName: editingValue.trim() };
      const response = await dispatch(updateCustomField(editingRowId, payload, pagination));
      if (response?.status === 200) {
        dispatch(success(response.data?.message || 'Custom field updated successfully'));
        // refresh current page
        dispatch(fetchCustomFields(pagination));
        setEditingRowId(null);
        setEditingValue('');
      } else if (
        response?.data?.message === MESSAGES.SETTINGS.CUSTOM_FIELDS.RESPONSE_ALREADY_EXIST
      ) {
        dispatch(failure(MESSAGES.SETTINGS.CUSTOM_FIELDS.FIELD_EXISTS));
      } else {
        dispatch(failure(response?.data?.message || 'Failed to update custom field'));
      }
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || 'Failed to update custom field'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingRowId(null);
    setEditingValue('');
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSave();
    } else if (event.key === 'Escape') {
      handleCancel();
    }
  };

  const getTypeTitle = (row) => {
    if (row.typeTitle) return row.typeTitle;
    const matchedType = customFieldTypes.find((fieldType) => fieldType.key === row.type);
    return matchedType?.title || row.type || '-';
  };

  const handleOpenEditModal = (row) => {
    setSelectedCustomField({
      id: row.id,
      value: row.value,
      type: row.type || 'single_text',
    });
    setEditModal(true);
    handleMenuClose();
  };

  const columns = [
    {
      field: 'value',
      headerName: 'Custom Field Name',
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => {
        const isEditing = editingRowId === params.row.id;
        return (
          <Box className="addressWrapper">
            {isEditing ? (
              <Box className="inlineEditWrapper">
                <TextField
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  onKeyDown={handleKeyPress}
                  autoFocus
                  variant="standard"
                  size="small"
                  className="inlineEditField"
                  disabled={saving}
                  style={{ width: '200px' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                        {editingValue && (
                          <IconButton
                            className="cpClearInputBtn"
                            size="small"
                            onClick={() => setEditingValue('')}
                            edge="end"
                            sx={{ padding: '4px' }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                <Box className="editActions">
                  <IconButton
                    onClick={handleSave}
                    disabled={saving}
                    size="small"
                    className="saveButton"
                  >
                    {saving ? (
                      <CircularProgress size={16} />
                    ) : (
                      <img src={CheckSettingsIcon} alt="save" />
                    )}
                  </IconButton>
                  <IconButton
                    onClick={handleCancel}
                    disabled={saving}
                    size="small"
                    className="cancelButton"
                  >
                    <img src={XSettingsIcon} alt="cancel" />
                  </IconButton>
                </Box>
              </Box>
            ) : (
              <Tooltip title={stringLength(params.row.value, 25) ? params.row.value : ''}>
                <Box className="tempValueWrapper">
                  <Typography className={editingRowId ? 'disabledText' : ''}>
                    {params.row.value}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'typeTitle',
      headerName: MESSAGES.SETTINGS.CUSTOM_FIELDS.TYPE,
      flex: 0.6,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => <Typography>{getTypeTitle(params.row)}</Typography>,
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const isMenuOpen = open && activeRowId?.id === params.row.id;
        return (
          <Box sx={{ textAlign: 'right', width: '100%' }}>
            <IconButton
              onClick={(e) => handleMenuOpen(e, params.row)}
              className="olcTableDropdownIcon"
              sx={{
                opacity: isMenuOpen ? '0.8 !important' : '0.4 !important',
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    if (sortedRows.length === 0 && currentPage > 1) {
      handleChangePage('', currentPage - 1);
    }
  }, [sortedRows]);

  // Add delay for showing no rows overlay
  useEffect(() => {
    let timeoutId;

    if (!(initialLoad || isLoading || paginationLoading) && sortedRows.length === 0) {
      // Show "No Results Found" after a delay when not loading and no data
      timeoutId = setTimeout(() => {
        setShowNoRows(true);
      }, 300); // 300ms delay
    } else {
      // Hide immediately when loading or when we have data
      setShowNoRows(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [initialLoad, isLoading, paginationLoading, sortedRows.length]);

  // Reset inline editing when pagination changes
  useEffect(() => {
    if (editingRowId !== null) {
      setEditingRowId(null);
      setEditingValue('');
      setSaving(false);
    }
  }, [currentPage, perPage]);

  return (
    <Box
      className={`olcTableGridInternal ${initialLoad || isLoading || paginationLoading ? 'table-loading' : ''} ${sortedRows.length === 0 ? 'table-empty' : ''}`}
    >
      <Box sx={{ position: 'relative' }}>
        <DataGrid
          rows={sortedRows}
          columns={columns}
          getRowId={(row) => row.id}
          disableColumnMenu
          disableRowSelectionOnClick
          pagination={false}
          rowHeight={61}
          loading={initialLoad || isLoading || paginationLoading}
          sx={{
            ...(showNoRows ||
            (!(initialLoad || isLoading || paginationLoading) && sortedRows.length === 0)
              ? {
                  height: 'auto',
                  minHeight: '200px',
                }
              : {}),
          }}
          getRowClassName={(params) => (params.id === editingRowId ? 'row-editing' : '')}
          slots={{
            noRowsOverlay: () =>
              showNoRows ? (
                <Typography className="noResultsText">No Results Found</Typography>
              ) : null,
            noResultsOverlay: () =>
              showNoRows ? (
                <Typography className="noResultsText">No Results Found</Typography>
              ) : null,
          }}
        />
      </Box>

      {(showLoader || rowsCount > 0) && (
        <OlcTablePagination
          count={lastPage}
          page={currentPage}
          pageSize={perPage}
          pageSizes={pageSizes}
          onPageChange={handleChangePage}
          onPageSizeChange={(event) => {
            setPaginationLoading(true);
            updatePagination({ page: 1, pageSize: event.target.value });
          }}
          from={from}
          to={to}
          total={rowsCount}
          loading={showLoader}
        />
      )}

      {/* Menu Actions */}
      <OlcActionMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* <MenuItem onClick={() => handleOpenEditModal(activeRowId)} disabled={editingRowId !== null}>
          <img src={GeneralPencilCIcon} alt="edit" />
          Edit
        </MenuItem> */}
        <MenuItem onClick={() => handleOpenEditModal(activeRowId)} disabled={editingRowId !== null}>
          <img src={GeneralPencilCIcon} alt="edit" />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleDelete(activeRowId)}>
          <img src={DeleteCIcon} alt="delete" />
          Delete
        </MenuItem>
      </OlcActionMenu>
    </Box>
  );
};

export default CustomTable;
